import { Box, IconButton, Slider, Tooltip, Typography } from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";
import GestureIcon from "@mui/icons-material/Gesture";
import PolylineIcon from "@mui/icons-material/Polyline";
import BrushIcon from "@mui/icons-material/Brush";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoFixOffIcon from "@mui/icons-material/AutoFixOff";
import DrawIcon from "@mui/icons-material/Draw";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import { useDataReview } from "../../contexts/DataReviewContext";
import { LINE, MONO, MUTED, TEAL, floatSx, railBtnSx } from "./editorTheme";
import { useEditorActions } from "./useEditorActions";
import { useEditorData } from "./useEditorData";
import { EditorTool, useVegEditor } from "./VegEditorContext";

type Item = { tool: EditorTool; label: string; title: string; icon: React.ReactNode; key?: string; disabled?: boolean };

/** The tool rail on the left of the map: select / lasso / edit outline /
 *  paint while reviewing; wand / unwand / draw / cut while editing one
 *  outline; brush / lasso fill / wand fill / revert while painting. The
 *  active tool's one setting (brush size, wand tolerance) opens beside it. */
export default function VegEditorRail() {
  const { mode, tool, brushM, setBrushM, tolerance, setTolerance } = useVegEditor();
  const { currentId } = useDataReview();
  const a = useEditorActions();
  const d = useEditorData();
  const items: Item[] = mode === "review" ? [
    { tool: "select", label: "Select", title: "Select a shape · drag to pan", icon: <NearMeIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />, key: "V" },
    { tool: "lasso", label: "Lasso select", title: "Lasso: drag around the shapes to select them", icon: <GestureIcon fontSize="small" />, key: "L" },
  ] : mode === "edit" ? [
    { tool: "wand", label: "Wand", title: "Wand: click to add similar ground", icon: <AutoFixHighIcon fontSize="small" />, key: "W" },
    { tool: "unwand", label: "Unwand", title: "Unwand: click to remove similar ground", icon: <AutoFixOffIcon fontSize="small" />, key: "U" },
    { tool: "draw", label: "Draw", title: "Draw: click around ground to add, then Enter", icon: <DrawIcon fontSize="small" />, key: "D" },
    { tool: "cut", label: "Cut", title: "Cut: click around ground to remove, then Enter", icon: <ContentCutIcon fontSize="small" />, key: "C" },
  ] : [
    { tool: "brush", label: "Brush", title: "Brush: drag to paint", icon: <BrushIcon fontSize="small" />, key: "B" },
    { tool: "poly", label: "Lasso fill", title: "Lasso fill: drag around ground to paint it", icon: <PentagonOutlinedIcon fontSize="small" />, key: "P" },
    { tool: "wand", label: "Wand fill", title: d.hasWand ? "Wand: click to fill similar ground" : "The wand works once the image is prepared", icon: <AutoFixNormalIcon fontSize="small" />, key: "W", disabled: !d.hasWand },
    { tool: "revert", label: d.isClean ? "Erase" : "Revert brush", title: d.isClean ? "Erase back to not mapped" : "Revert brush: back to what the layer has", icon: <PolylineIcon fontSize="small" />, key: "R" },
  ];
  const optIdx = items.findIndex((it) => it.tool === tool && (it.tool === "brush" || it.tool === "revert" || it.tool === "wand" || it.tool === "unwand"));
  const isBrush = tool === "brush" || tool === "revert";
  const extra = mode === "review" ? [
    { tool: "edit" as const, label: "Edit outline", title: "Edit the selected outline", icon: <DrawIcon fontSize="small" />, key: "E", disabled: currentId === null },
    { tool: "paint" as const, label: "Paint vegetation", title: "Paint the vegetation layer", icon: <BrushIcon fontSize="small" />, key: "" },
  ] : [];
  const btn = (it: Item | typeof extra[number], on: boolean, onClick: () => void) => (
    <Tooltip key={it.tool} title={it.title} placement="right">
      <span>
        <IconButton onClick={onClick} disabled={"disabled" in it && it.disabled} aria-label={it.label} sx={{ ...railBtnSx(on), position: "relative" }}>
          {it.icon}
          {it.key && <Typography component="span" sx={{ position: "absolute", right: 3, bottom: 2, fontFamily: MONO, fontSize: 9, lineHeight: 1, opacity: 0.75 }}>{it.key}</Typography>}
        </IconButton>
      </span>
    </Tooltip>
  );
  return (
    <Box sx={{ ...floatSx, position: "fixed", left: 12, top: "calc(var(--topbar-h) + (var(--vh100) - var(--topbar-h)) / 2)", transform: "translateY(-50%)", display: "flex", flexDirection: "column", gap: 0.25, p: 0.5, zIndex: 1090 }}>
      {items.map((it) => btn(it, tool === it.tool, () => a.setTool(it.tool)))}
      {extra.length > 0 && <Box sx={{ height: 1, bgcolor: LINE, mx: 0.75, my: 0.5 }} />}
      {extra.map((it) => btn(it, false, () => (it.tool === "edit" ? a.beginEdit() : a.toPaint())))}
      {optIdx >= 0 && (
        <Box sx={{ ...floatSx, position: "absolute", left: 54, top: 4 + optIdx * 42, width: 224, p: "10px 12px", zIndex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{isBrush ? (tool === "revert" ? (d.isClean ? "Erase size" : "Revert size") : "Brush size") : "Wand tolerance"}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{isBrush ? `${brushM} m` : tolerance}</Typography>
          </Box>
          {isBrush
            ? <Slider size="small" min={1} max={40} step={1} value={brushM} onChange={(_, v) => setBrushM(v as number)} sx={{ color: TEAL, mt: 0.5 }} />
            : <Slider size="small" min={2} max={60} step={1} value={tolerance} onChange={(_, v) => setTolerance(v as number)} sx={{ color: TEAL, mt: 0.5 }} />}
          <Typography sx={{ fontSize: 11, color: MUTED }}>{isBrush ? "Width of the stroke on the ground." : "How different the ground can be and still be picked up."}</Typography>
          <Box aria-hidden="true" sx={{ position: "absolute", left: -6, top: 15, width: 10, height: 10, bgcolor: "#212b37", borderLeft: `1px solid rgba(255,255,255,0.18)`, borderBottom: `1px solid rgba(255,255,255,0.18)`, transform: "rotate(45deg)" }} />
        </Box>
      )}
    </Box>
  );
}
